/* eslint-disable max-len */
/* eslint-disable max-lines */

import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import SkeletonTicket from "../../components/reservation_v2/SkeletonTicket";
import { IEsimProfile } from "../../interfaces/esim/EsimReservations.types";
import { getEsimProfile } from "../../services/Esim.service";

export const ESIM_STATUS_KEYS = [
  "Active",
  "Inactive",
  "Deleted",
  "Provisioned",
  "BPP installation"
];

export const getEsimProfileStatusLabel = (status: string) => {
  switch (status) {
    case "Provisioned":
      return "Gati për instalim";
    case "Deleted":
      return "I fshirë";
    case "Active":
      return "Aktiv";
    case "Inactive":
      return "Jo aktiv";
    case "BPP installation":
      return "Duke instaluar";
    default:
      return status;
  }
};

export const getEsimProfileStatusColor = (status: string) => {
  switch (status) {
    case "Provisioned":
      return "bg-blue text-white";
    case "Deleted":
      return "bg-red text-white";
    case "Active":
      return "bg-green text-white";
    case "Inactive":
      return "bg-gray text-white";
    case "BPP installation":
      return "bg-secondary text-white";
    default:
      return "bg-primary text-white";
  }
};

export const getEsimProfileStatusDescription = (status: string) => {
  switch (status) {
    case "Active":
      return "Klienti e ka instaluar dhe aktivizuar eSIM-in. Nëse shërbimi ende nuk funksionon, sigurohuni që Data Roaming është i ndezur në pajisje.";
    case "Inactive":
      return "Klienti e ka instaluar eSIM-in, por nuk e ka aktivizuar për përdorim të internetit, pasi nuk e ka ndezur opsionin “Turn on this line” në Settings e pajisjes.";
    case "Deleted":
      return "Klienti e ka fshirë eSIM-in nga pajisja e tij.";
    case "Provisioned":
      return "Klienti e ka blerë eSIM-in, por ende nuk ka filluar asnjë hap të procesit të instalimit apo aktivizimit.";
    case "BPP installation":
      return "Klienti është në proces të instalimit të eSIM-it, por instalimi ende nuk ka përfunduar.";
    default:
      return "";
  }
};

const EsimProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<IEsimProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (id) {
          const data = await getEsimProfile(parseInt(id));
          setProfile(data);
        }
      } catch (error) {
        console.error("Error fetching ESIM profile:", error);
      } finally {
        setLoading(false);
      }
    };

    void fetchProfile();
  }, [id]);

  if (loading) {
    return <SkeletonTicket loading={loading} />;
  }

  if (!profile) {
    return <div className="text-center text-red-500">Profile not found</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 flex-col w-full items-center">
        <div className="w-full lg:w-[70%]">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Kthehu</span>
            </button>
            <h1 className="text-2xl font-semibold">Detajet e profilit ESIM</h1>
          </div>
          <div className="bg-gray-100 p-6 rounded-lg shadow-sm">
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">
                    {profile.clientName}
                  </h2>
                  <div className="flex gap-4 mt-1">
                    <p className="text-sm text-gray-500">
                      ID: {profile.esimProfileId}
                    </p>
                    <p className="text-sm text-gray-500">
                      ICCID: {profile.iccid}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <div
                    className={`px-3 py-1 rounded-full text-sm font-medium
                  text-center  ${
                    profile.isActive
                      ? "bg-green text-white"
                      : "bg-red text-white"
                  }`}
                  >
                    {profile.isActive ? "Aktiv" : "Jo aktiv"}
                  </div>
                  {profile.isActive && profile.esimProfileStatus && (
                    <>
                      <div
                        className={`px-2 py-0.5 rounded-full mb-1 text-sm text-center cursor-pointer
                      font-medium ${getEsimProfileStatusColor(
                        profile.esimProfileStatus
                      )}`}
                        data-tooltip-content={getEsimProfileStatusDescription(
                          profile.esimProfileStatus
                        )}
                        data-tooltip-id="statusDescription"
                      >
                        🛜 {getEsimProfileStatusLabel(profile.esimProfileStatus)}
                      </div>
                      <Tooltip delayShow={2000} id="statusDescription" className="z-50" />
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="text-sm text-gray-600">Total i blerë</h3>
                  <p className="text-xl font-semibold">
                    €{profile.totalPurchasedEuro.toFixed(2)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="text-sm text-gray-600">Total i shpenzuar</h3>
                  <p className="text-xl font-semibold">
                    €{profile.totalSpentEuro.toFixed(2)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="text-sm text-gray-600">Zbritje totale</h3>
                  <p className="text-xl font-semibold">
                    €{profile.totalDiscountEuro.toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h3 className="text-sm text-gray-600 mb-2">
                  Të dhënat e përdorura
                </h3>
                <p className="text-xl font-semibold">
                  {(profile.totalUsedDataBytes / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold mb-4">Rezervimet</h3>
                <div className="space-y-4">
                  {profile.reservations.map((reservation) => (
                    <div
                      key={reservation.reservationId}
                      className="border-b pb-4 last:border-b-0"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">
                              {reservation.packageName}
                            </p>
                            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                              ID: {reservation.reservationId}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {new Date(reservation.insertDate).toLocaleString(
                              "sq-AL",
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit"
                              }
                            )}
                          </p>
                        </div>
                        <p className="font-semibold">
                          €{reservation.paidAmount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EsimProfile;
